
import React, { useState, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useNavigate } from 'react-router-dom';
import { loginUser } from '@/services/firebase';

interface LoginFormProps {
  isSubmitting: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({ isSubmitting }) => {
  const navigate = useNavigate();
  const [isInIframe, setIsInIframe] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const [loginData, setLoginData] = useState({
    email: '',
    referrerCode: ''
  });
  
  useEffect(() => {
    // Check if running in iframe
    setIsInIframe(window.top !== window.self);
    
    // Check URL for login params (for direct links)
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');
    const codeParam = urlParams.get('code');
    
    if (emailParam && codeParam) {
      setLoginData({
        email: emailParam,
        referrerCode: codeParam
      });
    }
  }, []);
  
  const handleLoginInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!navigator.onLine) {
      toast.error("You appear to be offline. Please check your internet connection.");
      return;
    }
    
    try {
      setIsLoggingIn(true);
      const { email, referrerCode } = loginData;
      
      // Login with email and referral code as password
      await loginUser(email, referrerCode);
      
      // Store referrer code in localStorage
      localStorage.setItem('referrerCode', referrerCode);
      
      // If in iframe, send message to parent window
      if (isInIframe) {
        window.parent.postMessage({ 
          type: 'login-success', 
          userData: {
            email,
            referrerCode
          } 
        }, '*');
      }
      
      // Navigate to dashboard
      navigate('/dashboard');
      
      toast.success("Login successful");
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.message || "Invalid email or referrer code");
      
      // If in iframe, send message to parent window about login failure
      if (isInIframe) {
        window.parent.postMessage({ 
          type: 'login-error', 
          error: "Invalid credentials"
        }, '*');
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <Card className="glass-panel">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-center">Welcome Back</CardTitle>
        <CardDescription className="text-center text-base">
          Login to manage your referrals
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleLoginSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="loginEmail" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="loginEmail"
              name="email"
              type="email"
              placeholder="john@example.com"
              required
              className="input-style"
              value={loginData.email}
              onChange={handleLoginInputChange}
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="loginReferrerCode" className="text-sm font-medium">
              Referrer Code
            </label>
            <Input
              id="loginReferrerCode"
              name="referrerCode"
              placeholder="Your unique code"
              required
              className="input-style"
              value={loginData.referrerCode}
              onChange={handleLoginInputChange}
            />
          </div>
          
          <Button
            type="submit"
            className="w-full h-12 glass-button text-lg mt-4"
            disabled={isSubmitting || isLoggingIn}
          >
            {isSubmitting || isLoggingIn ? "Processing..." : "Login"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default LoginForm;
